import { createPortal } from 'react-dom';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { InvoiceFormFields } from '@/modules/cart/domain/Cart';
import { addInvoice } from '@/modules/cart/infrastructure/CartReduxRepository/CartReduxRepository';
import { ModalWrapper } from '@/modules/perseo-billing/components';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks/use-gtm-billing';
import useToast from '@/modules/perseo-billing/hooks/use-toast';
import { setIsDeleteCompanyModalOpen } from '@/modules/perseo-billing/store/slices/billing';
import {
  Card,
  CardHeader,
  Divider,
  Icon,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { PdsButton } from '@/modules/perseo-billing/ui/react/src/components/gamma';
import useRegions from '@/sections/region/hooks/useRegions';

export const DeleteCompanyModal = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const isDeleteCompanyModalOpen = useAppSelector(
    (state) => state.billing.isDeleteCompanyModalOpen
  );

  const { data: cart } = useAppSelector((state) => state.cart);

  const { getRegionAndCommuneByIds } = useRegions();

  const { successfullyDeletedCompanyGtm, deleteCompanyGoBackGtm } =
    useGtmBillingProduct();
  const deleteCompanyHandler = () => {
    const { shippingAddress } = cart ?? {};
    const region = getRegionAndCommuneByIds(
      shippingAddress?.region,
      shippingAddress?.commune
    );

    const commune = region?.communes?.[0];

    const payload: InvoiceFormFields = {
      ...shippingAddress,
      region: shippingAddress?.region?.toString(),
      isInvoice: false,
      regionName: region?.name,
      communeName: commune?.name
    };
    dispatch(addInvoice(payload));
    closeModalAfterDeletingCompany();
    toast(
      'Borraste tus datos de facturación',
      'Emitiremos una boleta en su lugar',
      'success'
    );
  };

  // * There are 2 functions that close modal for analytic reasons
  const closeModalAfterDeletingCompany = () => {
    successfullyDeletedCompanyGtm();
    dispatch(setIsDeleteCompanyModalOpen(false));
  };

  const closeModal = () => {
    deleteCompanyGoBackGtm();
    dispatch(setIsDeleteCompanyModalOpen(false));
  };

  const modalContent = (
    <ModalWrapper
      size="sm"
      open={isDeleteCompanyModalOpen}
      onClose={closeModal}
      roundedType="FIXED_BOTTOM"
    >
      <Card p={16}>
        <CardHeader
          pb={16}
          action={
            <div onClick={closeModal}>
              <Icon icon="CloseIcon" width={18} height={18} />
            </div>
          }
          title={
            <div className="co-flex co-gap-8px">
              <Icon
                icon="InputWarningIcon"
                width={24}
                height={24}
                className="co-min-w-24px co-min-h-24px"
              />
              <Typography size="h2" weight="semibold">
                ¿Quieres eliminar esta empresa?
              </Typography>
            </div>
          }
        />
        <Divider />
        <div className="co-flex co-flex-col co-gap-16px co-pt-16px">
          <Typography>Se borrarán los datos asociados.</Typography>
          <PdsButton
            onClick={() => dispatch(deleteCompanyHandler)}
            typology="primary"
            feedback="error"
          >
            Eliminar
          </PdsButton>
          <PdsButton typology="secondary" onClick={closeModal}>
            Volver
          </PdsButton>
        </div>
      </Card>
    </ModalWrapper>
  );

  const rootModalDeleteCompany = document.getElementById(
    'modal-delete-company'
  );
  return (
    rootModalDeleteCompany && createPortal(modalContent, rootModalDeleteCompany)
  );
};
